import { useEffect } from 'react';
import { useAuthStore } from '@stores/auth.store';
import { PORTAL, subscribeAuthState, subscribeStaffDoc } from '@services/registry';
import { getFirebaseApp } from '@services/firebase/app';

/**
 * Keeps Zustand auth in sync with Firebase Auth (session restore, tab sync, sign-out).
 */
export function useFirebaseAuthListener() {
    const applyFirebaseSession = useAuthStore((s) => s.applyFirebaseSession);

    useEffect(() => {
        let unsubscribe = () => {};
        let unsubscribeStaff = () => {};

        try {
            getFirebaseApp();
        } catch (e) {
            console.warn('[School CRM]', e.message);
            useAuthStore.setState({ isRefreshing: false });
            return;
        }

        unsubscribe = subscribeAuthState(async (user) => {
            useAuthStore.setState({ isRefreshing: true });
            try {
                await applyFirebaseSession(user);

                // Live-refresh staff permissions so sidebar + routes react immediately.
                unsubscribeStaff();
                unsubscribeStaff = () => {};
                const state = useAuthStore.getState();
                const u = state.user;
                const perms = Array.isArray(u?.permissions) ? u.permissions : [];
                if (u?.role === PORTAL.TENANT && u?.tenantId && u?.uid) {
                    unsubscribeStaff = subscribeStaffDoc({
                        tenantId: u.tenantId,
                        staffId: u.uid,
                        onNext: (staffData) => {
                            if (!staffData) return;

                            const access = staffData?.authorities?.access || {};
                            const permissionFromAccess = (resourceKey) => {
                                const v = access?.[resourceKey];
                                if (v === 'view') return `${resourceKey}.view`;
                                if (v === 'full') return `${resourceKey}.full`;
                                if (v === 'admin') return `${resourceKey}.full`;
                                return null;
                            };
                            const derived = [
                                permissionFromAccess('dashboard'),
                                permissionFromAccess('students'),
                                permissionFromAccess('teachers'),
                                permissionFromAccess('staff'),
                                permissionFromAccess('tenantProfile'),
                                permissionFromAccess('classes'),
                                permissionFromAccess('schoolTime'),
                                permissionFromAccess('preferences'),
                                permissionFromAccess('studentAttendance'),
                                permissionFromAccess('teacherAttendance'),
                                permissionFromAccess('staffAttendance'),
                                permissionFromAccess('financeAccounts'),
                                permissionFromAccess('receiveLedger'),
                                permissionFromAccess('expenseLedger'),
                                permissionFromAccess('transactionLedger'),
                                permissionFromAccess('feesManagement'),
                            ].filter(Boolean);

                            useAuthStore.setState((s) => ({
                                user: s.user ? { ...s.user, permissions: derived } : s.user,
                            }));
                        },
                        onError: (err) => console.error('[School CRM] staff onSnapshot error:', err),
                    });
                }
            } catch (e) {
                // Don't crash the app on permission / rules issues during bootstrap.
                // Log so it can be debugged in browser console.
                console.error('[School CRM] Auth bootstrap failed:', e);
            } finally {
                useAuthStore.setState({ isRefreshing: false });
            }
        });

        return () => {
            unsubscribeStaff();
            unsubscribe();
        };
    }, [applyFirebaseSession]);
}
